import { useRef } from 'react';

export interface LatestRequestHandlers<T> {
  /** Called with the result only when this request is still the latest for its key. */
  onApply: (result: T) => void;
  /** Called with the error only when this request is still the latest for its key. */
  onError: (error: unknown) => void;
  /** Called once the latest request for the key has settled (e.g. to clear a loading flag). */
  onSettled?: () => void;
}

export type RunLatestRequest = <T>(
  key: string,
  request: () => Promise<T>,
  handlers: LatestRequestHandlers<T>
) => Promise<void>;

interface KeyState {
  latestId: number;
  /** Callers still waiting for the key's latest request to settle. */
  waiters: Array<() => void>;
}

/**
 * Creates a tracker that keeps only the latest request per key in charge.
 * An older request's result and error are ignored. Every returned promise resolves when the key's
 * latest request settles, even if an older request's network call is still in flight, so anyone
 * awaiting it ends up with current data and isn't held up by obsolete requests.
 * The returned promise never rejects; errors go to `onError`.
 */
export function createLatestRequestTracker(): RunLatestRequest {
  const keys: Record<string, KeyState> = {};

  return <T>(key: string, request: () => Promise<T>, handlers: LatestRequestHandlers<T>) => {
    const state = keys[key] ?? (keys[key] = { latestId: 0, waiters: [] });
    const id = ++state.latestId;
    const isLatest = () => state.latestId === id;

    return new Promise<void>((resolve) => {
      state.waiters.push(resolve);

      // Wrapping in a promise turns a synchronous throw from `request` into a rejection
      new Promise<T>((resolveRequest) => resolveRequest(request()))
        .then((result) => {
          if (isLatest()) handlers.onApply(result);
        })
        .catch((error: unknown) => {
          if (isLatest()) handlers.onError(error);
        })
        .finally(() => {
          if (!isLatest()) return;
          try {
            handlers.onSettled?.();
          } finally {
            const waiters = state.waiters;
            state.waiters = [];
            waiters.forEach((release) => release());
          }
        });
    });
  };
}

/**
 * Hook wrapper around {@link createLatestRequestTracker}; the tracker is stable for the component's lifetime.
 */
export function useLatestRequestPerKey(): RunLatestRequest {
  const trackerRef = useRef<RunLatestRequest | null>(null);
  if (!trackerRef.current) {
    trackerRef.current = createLatestRequestTracker();
  }
  return trackerRef.current;
}
